import { WorklistValues } from "features/WorklistTools/shared/interfaces";
import { Worklist } from "../shared/interfaces";
import {
  getColIndexFromSelectedWell,
  getRowIndexFromSelectedWell,
  getSourceWellKey,
} from "../shared/WorklistHelpers";
import {
  DestPlateInfoState,
  SourcePlateInfoState,
  WorklistValuesState,
} from "./state";
import {
  ITwelve24TCMethodSettings,
  ITwelve24TCToolInternalState,
} from "./state/initial-state";

export enum CellNumberUpdateType {
  ALL = "All",
  EMPTY = "Empty",
  INDIVIDUAL = "Individual",
}

export const buildHarvestWellsWorklist = (
  harvestWells: any[],
  sourcePlateInfo: any[],
  destPlateInfo: any[],
  methodSettings: any,
) => {
  const worklist: any[] = [];
  for (const row of harvestWells) {
    const currentSourcePlate = sourcePlateInfo[row.sourcePlateIndex];
    const currentDestPlate = destPlateInfo[row.destPlateIndex];
    const index = currentSourcePlate.wellInfo.findIndex(
      (e: any) =>
        e.rowPos - 1 === getRowIndexFromSelectedWell(row.sourceWellId) &&
        e.colPos - 1 === getColIndexFromSelectedWell(row.sourceWellId),
    );
    if (index > -1)
      worklist.push({
        task: "HarvestWells",
        details: "",
        aspirateVol: "",
        dispenseVol: "",
        transferVol: row.transferVol.toString(),
        sourcePlateType: currentSourcePlate.labwareTypeCode,
        sourcePlateBarcode: currentSourcePlate.plateBarcode,
        sourceWellID: row.sourceWellId,
        destinationPlateType: currentDestPlate.labwareTypeCode,
        destinationPlateBarcode: currentDestPlate.plateBarcode,
        destinationWellID: row.destWellId,
      });
  }
  return worklist;
};

export const buildDiscardSourcePlateWorklist = (
  sourcePlateInfo: SourcePlateInfoState[],
) => {
  const worklist: Worklist[] = [];
  for (let i = 0; i < sourcePlateInfo.length; i++) {
    if (sourcePlateInfo[i].plateBarcode !== "") {
      worklist.push({
        task: "SourcePlate",
        details: `Discard|${
          sourcePlateInfo[i].discardSourcePlate.toString() ?? ""
        };`,
        sourcePlateType: sourcePlateInfo[i].labwareTypeCode,
        sourcePlateBarcode: sourcePlateInfo[i].plateBarcode,
      });
    }
  }
  return worklist;
};

export const buildMethodSettingsWorklist = (
  twelve24TCState: ITwelve24TCToolInternalState,
  worklistValues: WorklistValuesState,
  buildToDownload = true, //true = worklist, false = template
) => {
  const methodSettings = twelve24TCState.methodSettings;
  const worklist: Worklist[] = [];

  worklist.push({
    task: "Dissociation",
    details: `Enable|${(+methodSettings.dissociation).toString()};EnableWash|${(+methodSettings.washBeforeDissociation).toString()};WashReagentPosition|${methodSettings.dissociationWashRGT.toString()};`,
  });

  worklist.push({
    task: "Incubation",
    details: `IncubationTimeSeconds|${
      buildToDownload
        ? methodSettings.dissociationTime
          ? (methodSettings.dissociationTime * 60).toString()
          : ""
        : methodSettings.dissociationTime?.toString()
    };`,
  });

  worklist.push({
    task: "Spin",
    details: `AutomatedSpin|${(+!userShouldManuallySpin(
      worklistValues,
    )).toString()};VSpinRPM|${methodSettings.spinParamPercent.toString()};VSpinTime|${
      buildToDownload
        ? (methodSettings.spinTime * 60).toString()
        : methodSettings.spinTime.toString()
    };VSpinAccel|${methodSettings.spinAccel.toString()};VSpinDecel|${methodSettings.spinDecel.toString()};`,
  });

  // pelletResuspensionRGT === 5 should still input 4 into the worklist
  const getReagentPosition = () =>
    (methodSettings.pelletResuspensionRGT === 5
      ? 4
      : methodSettings.pelletResuspensionRGT
    ).toString();

  const preSplitTimerSeconds =
    methodSettings.prePelletSplitTimer === null ||
    methodSettings.prePelletSplitTimer === undefined
      ? ""
      : buildToDownload
      ? (methodSettings.prePelletSplitTimer * 60).toString()
      : methodSettings.prePelletSplitTimer.toString();

  worklist.push({
    task: "PelletResuspension",
    details: `ReagentPosition|${getReagentPosition()};ReagentVolume|${
      methodSettings.pelletResuspensionVol === null
        ? "0"
        : methodSettings.pelletResuspensionVol!.toString()
    };IsDNAShield|${Number(
      methodSettings.pelletResuspensionRGT === 5,
    )};PreSplitTimer|${preSplitTimerSeconds};`,
  });

  return worklist;
};

export const buildTopUpPostSplitWorklist = (
  destPlateInfo: DestPlateInfoState[],
) => {
  const worklist: Worklist[] = [];
  worklist.push({
    task: "TopUp",
    details: `Volume|${destPlateInfo[0].topupVol.toString()};`,
  });
  return worklist;
};

export const buildDeadTotalPlateWorklist = (
  deadPlateBarcode: string,
  deadPlateType: string,
  methodSettings: ITwelve24TCMethodSettings,
) => {
  const worklist: Worklist[] = [];
  worklist.push({
    task: "DeadTotalPlate",
    details: `CountPreSpin|${methodSettings.deadTotalCountSpinOrder ?? ""};`,
    transferVol: methodSettings.aliquotVolume.toString(),
    sourcePlateType: deadPlateType,
    sourcePlateBarcode: deadPlateBarcode,
  });

  return worklist;
};

export const buildMappingFileWorklist = (
  sourcePlateInfo: SourcePlateInfoState[],
  destPlateInfo: DestPlateInfoState[],
) => {
  const worklist: Worklist[] = [];
  const uniqueSourcePlateTypes = [
    ...new Set(
      sourcePlateInfo
        .map((e) => e.labwareTypeCode)
        .filter((labware) => labware !== ""),
    ),
  ];
  const uniqueDestPlateTypes = [
    ...new Set(
      destPlateInfo
        .map((e) => e.labwareTypeCode)
        .filter((labware) => labware !== ""),
    ),
  ];

  const srcPlateCode =
    uniqueSourcePlateTypes.length > 1
      ? "X"
      : sourcePlateInfo[0].plateBarcode.split("_")[1];
  const destPlateCode =
    uniqueDestPlateTypes.length > 1
      ? "Y"
      : destPlateInfo[0].plateBarcode.split("_")[1];
  const deadPlateCode = "Dead" + srcPlateCode + "-" + destPlateCode;
  worklist.push({
    task: "SrcToDestMappingFileKeyword",
    sourcePlateBarcode: "_" + srcPlateCode + "_to_" + destPlateCode + "_",
  });

  worklist.push({
    task: "DestToDestMappingFileKeyword",
    sourcePlateBarcode: "_" + destPlateCode + "_to_" + destPlateCode + "_",
  });

  worklist.push({
    task: "DestToDeadMappingFileKeyword",
    sourcePlateBarcode: "_" + destPlateCode + "_to_" + deadPlateCode + "_",
  });

  return worklist;
};

export const getNumberOfAdditionalTubes = (
  worklistValues: WorklistValuesState,
) => {
  const harvestWells = worklistValues.harvestWells;
  const existingSourceWells = new Set<string>();
  let additionalTubeCount = 0;
  for (const row of harvestWells) {
    const sourceWellKey = getSourceWellKey(row);
    if (existingSourceWells.has(sourceWellKey)) {
      additionalTubeCount++;
    } else {
      existingSourceWells.add(sourceWellKey);
    }
  }
  return additionalTubeCount;
};

export const prvDividedByMaxDestWellDict = (
  harvestWells: WorklistValues[],
  methodSettings: any,
) => {
  const srcWellIDDict: Map<string, [number, number]> = new Map();
  for (const harvestWell of harvestWells) {
    const srcWellIDKey = getSourceWellKey(harvestWell);
    const [numDestWells] = srcWellIDDict.get(srcWellIDKey) ?? [0];
    srcWellIDDict.set(srcWellIDKey, [
      numDestWells + 1,
      methodSettings.pelletResuspensionVol / (numDestWells + 1),
    ]);
  }
  return srcWellIDDict;
};

export const errorPRVDivDestWell = (
  worklistValues: any,
  methodSettings: any,
  wellMapping: WorklistValues[] = [],
) => {
  const harvestAndIncomingWells = [...worklistValues.harvestWells];
  if (wellMapping.length > 0) {
    harvestAndIncomingWells.push(...wellMapping);
  }
  const prvDivByMaxDestDict = prvDividedByMaxDestWellDict(
    harvestAndIncomingWells,
    methodSettings,
  );
  for (const [, volPerDestWell] of prvDivByMaxDestDict.values()) {
    if (volPerDestWell !== 0 && volPerDestWell < 20) {
      return "You are splitting across wells resulting in transfers of less than 20 µL. \
            Please remove splits or increase resuspension volume.";
    }
  }
  return "";
};

export const userShouldManuallySpin = (worklistValues: any) => {
  const additionalTubes = getNumberOfAdditionalTubes(worklistValues);
  if (additionalTubes >= 10) return true;
  return false;
};

export const topUpPresent = (destPlateInfo: DestPlateInfoState[]) => {
  if (isNaN(destPlateInfo[0].topupVol)) {
    return "Topup volume is a required field";
  }
  return "";
};

export const topUpNotZero = (
  methodSettings: any,
  destPlateInfo: DestPlateInfoState[],
) => {
  if (
    methodSettings.pelletResuspensionVol !== 0 &&
    destPlateInfo[0].topupVol === 0
  ) {
    return "Top Up Volume can't be 0";
  }
  return "";
};

export const topupCalc = (
  worklistValues: any,
  methodSettings: any,
  destPlateInfo: DestPlateInfoState[],
  wellMapping: WorklistValues[] = [],
) => {
  const harvestAndIncomingWells = [...worklistValues.harvestWells];
  if (wellMapping.length > 0) {
    harvestAndIncomingWells.push(...wellMapping);
  }

  const prvDivByMaxDestWellDict = prvDividedByMaxDestWellDict(
    harvestAndIncomingWells,
    methodSettings,
  );
  const numOfTotalDestWells: number = harvestAndIncomingWells.length;
  if (
    numOfTotalDestWells === 0 &&
    destPlateInfo[0]!.topupVol < methodSettings.pelletResuspensionVol &&
    destPlateInfo[0]!.topupVol > 0
  )
    return "The Target Final Tube Volume cannot be less than the dest well volume of least-split source well(s). Check your splits or increase your Target Final Tube Volume.";

  let highestSplit = -1;
  for (const [key, val] of prvDivByMaxDestWellDict) {
    if (val[1] > highestSplit) {
      highestSplit = val[1];
      if (
        !isNaN(destPlateInfo[0].topupVol!) ||
        destPlateInfo[0].topupVol! != undefined
      ) {
        if (
          destPlateInfo[0]!.topupVol > 0 &&
          destPlateInfo[0]!.topupVol < highestSplit
        ) {
          return "The Target Final Tube Volume cannot be less than the dest well volume of least-split source well(s). Check your splits or increase your Target Final Tube Volume.";
        }
      }
    }
  }
  return "";
};

export const currentWellSplits = (worklistValues: any, methodSettings: any) => {
  const harvestWells = worklistValues.harvestWells;

  const prvDivByMaxDestWellDict = prvDividedByMaxDestWellDict(
    harvestWells,
    methodSettings,
  );

  for (const [sourceWellKey, destTupleVal] of prvDivByMaxDestWellDict) {
    const numDestWells = destTupleVal[0];
    if (numDestWells > 1) {
      return true;
    }
  }
  return false;
};
